import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash, PencilSimple, Buildings, Stack, MapPin, House } from "@phosphor-icons/react";

const ROLES = ["admin", "ops_manager", "supervisor", "staff", "auditor"];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  // Master lists for scope picker
  const [hospitals, setHospitals] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [areas, setAreas] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);

  const load = async () => {
    setLoading(true);
    const [u, h, b, f, a, r] = await Promise.all([
      api.get("/users"),
      api.get("/masters/hospitals"),
      api.get("/masters/buildings"),
      api.get("/masters/floors"),
      api.get("/masters/areas"),
      api.get("/roles"),
    ]);
    setUsers(u.data); setHospitals(h.data); setBuildings(b.data); setFloors(f.data); setAreas(a.data);
    setCustomRoles(r.data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ active: true, role: "staff", hospital_ids: [], building_ids: [], floor_ids: [], area_ids: [] });
    setModal({ item: null }); setErr("");
  };
  const openEdit = (u) => {
    setForm({
      ...u,
      password: "",
      hospital_ids: u.hospital_ids || [],
      building_ids: u.building_ids || [],
      floor_ids: u.floor_ids || [],
      area_ids: u.area_ids || [],
    });
    setModal({ item: u }); setErr("");
  };

  const save = async () => {
    try {
      if (modal.item) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.patch(`/users/${modal.item.id}`, payload);
      } else {
        await api.post("/users", form);
      }
      setModal(null); load();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const toggle = (key, id) => {
    setForm((f) => {
      const set = new Set(f[key] || []);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...f, [key]: Array.from(set) };
    });
  };

  const scopeSummary = (u) => {
    const h = (u.hospital_ids || []).length, b = (u.building_ids || []).length;
    const fl = (u.floor_ids || []).length, a = (u.area_ids || []).length;
    if (!h && !b && !fl && !a) return "All";
    return `${h}H / ${b}B / ${fl}F / ${a}A`;
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-small text-blue-700">Access Control</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>Users</h1>
          <p className="text-sm text-slate-600 mt-1">Manage users and their hospital / building / floor / area scope.</p>
        </div>
        <button data-testid="new-user-button" onClick={openCreate} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md inline-flex items-center gap-2 transition-colors">
          <Plus size={14} weight="bold" /> Add user
        </button>
      </header>

      <div className="flat-card">
        <table className="data-table">
          <thead><tr><th>Username</th><th>Name</th><th>Role</th><th>Email</th><th>Scope</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="text-center py-6 text-slate-500">Loading…</td></tr> :
              users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.username}`}>
                  <td className="font-semibold text-slate-900 font-mono">{u.username}</td>
                  <td>{u.name}</td>
                  <td><span className="label-small text-blue-700">{u.role}</span></td>
                  <td className="text-xs text-slate-600">{u.email || "—"}</td>
                  <td className="text-xs font-mono text-slate-700">{scopeSummary(u)}</td>
                  <td>{u.active ? <span className="label-small text-green-700">Active</span> : <span className="label-small text-slate-500">Inactive</span>}</td>
                  <td className="text-right">
                    <button onClick={() => openEdit(u)} className="p-1.5 rounded text-slate-500 hover:text-blue-600 transition-colors"><PencilSimple size={15} /></button>
                    <button onClick={() => remove(u.id)} className="p-1.5 rounded text-slate-500 hover:text-red-600 transition-colors"><Trash size={15} /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-slate-200 rounded-md max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="user-modal">
            <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Manrope" }}>{modal.item ? "Edit" : "New"} User</h3>
            <div className="grid grid-cols-2 gap-3">
              {!modal.item && (
                <div className="col-span-2">
                  <label className="label-small block mb-1">Username</label>
                  <input data-testid="user-username" value={form.username || ""} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600" />
                </div>
              )}
              <div>
                <label className="label-small block mb-1">Name</label>
                <input data-testid="user-name" value={form.name || ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600" />
              </div>
              <div>
                <label className="label-small block mb-1">Email</label>
                <input value={form.email || ""} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600" />
              </div>
              <div>
                <label className="label-small block mb-1">Role (built-in)</label>
                <select data-testid="user-role" value={form.role || "staff"} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600">
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="label-small block mb-1">Custom Role (optional, overrides UI)</label>
                <select data-testid="user-custom-role" value={form.custom_role || ""} onChange={(e) => setForm((f) => ({ ...f, custom_role: e.target.value || null }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600">
                  <option value="">— None (use built-in role) —</option>
                  {customRoles.map((r) => <option key={r.id} value={r.name}>{r.display_name || r.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-small block mb-1">{modal.item ? "New Password (optional)" : "Password"}</label>
                <input data-testid="user-password" type="password" value={form.password || ""} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600" />
              </div>
            </div>

            <div className="divider my-5" />

            <div className="space-y-4">
              <div className="label-small">Location scope (leave all empty for full access)</div>
              <ScopePicker icon={Buildings} title="Hospitals" items={hospitals} selected={form.hospital_ids} onToggle={(id) => toggle("hospital_ids", id)} keyName="hospital" />
              <ScopePicker icon={House} title="Buildings" items={buildings} selected={form.building_ids} onToggle={(id) => toggle("building_ids", id)} keyName="building" />
              <ScopePicker icon={Stack} title="Floors" items={floors} selected={form.floor_ids} onToggle={(id) => toggle("floor_ids", id)} keyName="floor" />
              <ScopePicker icon={MapPin} title="Areas" items={areas} selected={form.area_ids} onToggle={(id) => toggle("area_ids", id)} keyName="area" />
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700 mt-4">
              <input type="checkbox" checked={!!form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} /> Active
            </label>

            {err && <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

            <div className="flex justify-end gap-2 pt-5 mt-4 border-t border-slate-100">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-md transition-colors">Cancel</button>
              <button data-testid="user-save" onClick={save} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md transition-colors">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ScopePicker({ icon: Icon, title, items, selected = [], onToggle, keyName }) {
  return (
    <details className="border border-slate-200 rounded-md" data-testid={`scope-${keyName}`}>
      <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-slate-800 flex items-center justify-between hover:bg-slate-50 select-none">
        <span className="flex items-center gap-2"><Icon size={14} /> {title}</span>
        <span className="text-xs text-slate-500">{selected.length} selected</span>
      </summary>
      <div className="px-3 py-2 max-h-40 overflow-y-auto space-y-1 border-t border-slate-100">
        {items.length === 0 ? <div className="text-xs text-slate-400 italic">No items defined.</div> : items.map((it) => (
          <label key={it.id} className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 cursor-pointer">
            <input type="checkbox" checked={selected.includes(it.id)} onChange={() => onToggle(it.id)} data-testid={`scope-${keyName}-${it.id}`} />
            <span>{it.name}{it.code ? ` · ${it.code}` : ""}</span>
          </label>
        ))}
      </div>
    </details>
  );
}
